// Facility type mapping between display names and database values

export interface FacilityTypeMapping {
  displayName: string;
  dbValue: string;
  category: string;
  description: string;
}

// Database facility type values from backend
export const DB_FACILITY_TYPES = [
  "facility_complex",
  "factory",
  "production_line",
  "research_and_development",
  "office",
  "power_plant",
  "recycling",
  "mine",
  "oil_rig",
  "refinery",
  "store",
  "service_center",
  "distribution_center",
  "warehouse",
  "infrastructure",
  "road_or_bridge",
  "data_center",
  "port_or_harbor",
  "airport",
  "rail_or_rail_station",
  "exhibition_booth",
  "residential_building",
  "education",
  "military",
  "healthcare",
  "agriculture",
  "government",
  "other_facilities",
] as const;

export type DbFacilityType = (typeof DB_FACILITY_TYPES)[number];

// Comprehensive mapping between display names and database values
export const FACILITY_TYPE_MAPPINGS: FacilityTypeMapping[] = [
  // Manufacturing & Production
  {
    displayName: "Manufacturing Complex",
    dbValue: "facility_complex",
    category: "Manufacturing",
    description:
      "Large-scale manufacturing facilities with multiple operations",
  },
  {
    displayName: "Factory",
    dbValue: "factory",
    category: "Manufacturing",
    description: "Industrial production facilities",
  },
  {
    displayName: "Production Line",
    dbValue: "production_line",
    category: "Manufacturing",
    description: "Specialized production line facilities",
  },
  {
    displayName: "Refinery",
    dbValue: "refinery",
    category: "Manufacturing",
    description: "Oil, gas, and chemical refining facilities",
  },
  {
    displayName: "Recycling Facility",
    dbValue: "recycling",
    category: "Manufacturing",
    description: "Waste processing and recycling centers",
  },

  // Energy & Utilities
  {
    displayName: "Power Plant",
    dbValue: "power_plant",
    category: "Energy",
    description: "Electricity generation facilities",
  },
  {
    displayName: "Oil Rig",
    dbValue: "oil_rig",
    category: "Energy",
    description: "Offshore and onshore oil drilling platforms",
  },

  // Logistics & Distribution
  {
    displayName: "Warehouse",
    dbValue: "warehouse",
    category: "Logistics",
    description: "Storage and inventory facilities",
  },
  {
    displayName: "Distribution Center",
    dbValue: "distribution_center",
    category: "Logistics",
    description: "Product distribution and fulfillment centers",
  },
  {
    displayName: "Service Center",
    dbValue: "service_center",
    category: "Logistics",
    description: "Customer service and support facilities",
  },

  // Transportation & Infrastructure
  {
    displayName: "Port/Harbor",
    dbValue: "port_or_harbor",
    category: "Transportation",
    description: "Maritime shipping and cargo facilities",
  },
  {
    displayName: "Airport",
    dbValue: "airport",
    category: "Transportation",
    description: "Aviation facilities and terminals",
  },
  {
    displayName: "Rail Station",
    dbValue: "rail_or_rail_station",
    category: "Transportation",
    description: "Railway terminals and stations",
  },
  {
    displayName: "Road/Bridge",
    dbValue: "road_or_bridge",
    category: "Transportation",
    description: "Transportation infrastructure",
  },
  {
    displayName: "Infrastructure",
    dbValue: "infrastructure",
    category: "Transportation",
    description: "General infrastructure facilities",
  },

  // Technology & Research
  {
    displayName: "Research & Development",
    dbValue: "research_and_development",
    category: "Technology",
    description: "R&D centers and laboratories",
  },
  {
    displayName: "Data Center",
    dbValue: "data_center",
    category: "Technology",
    description: "IT infrastructure and server facilities",
  },

  // Commercial & Retail
  {
    displayName: "Store/Retail",
    dbValue: "store",
    category: "Commercial",
    description: "Retail stores and commercial outlets",
  },
  {
    displayName: "Office",
    dbValue: "office",
    category: "Commercial",
    description: "Corporate offices and business centers",
  },
  {
    displayName: "Exhibition Booth",
    dbValue: "exhibition_booth",
    category: "Commercial",
    description: "Trade shows and exhibition facilities",
  },

  // Extractive Industries
  {
    displayName: "Mine",
    dbValue: "mine",
    category: "Extractive",
    description: "Mining and extraction operations",
  },

  // Public & Institutional
  {
    displayName: "Government",
    dbValue: "government",
    category: "Public",
    description: "Government facilities and offices",
  },
  {
    displayName: "Military",
    dbValue: "military",
    category: "Public",
    description: "Military bases and defense facilities",
  },
  {
    displayName: "Healthcare",
    dbValue: "healthcare",
    category: "Public",
    description: "Hospitals and medical facilities",
  },
  {
    displayName: "Education",
    dbValue: "education",
    category: "Public",
    description: "Schools, universities, and training centers",
  },

  // Residential & Agriculture
  {
    displayName: "Residential",
    dbValue: "residential_building",
    category: "Residential",
    description: "Housing and residential complexes",
  },
  {
    displayName: "Agriculture",
    dbValue: "agriculture",
    category: "Agriculture",
    description: "Farms and agricultural facilities",
  },

  // Other
  {
    displayName: "Other",
    dbValue: "other_facilities",
    category: "Other",
    description: "Miscellaneous facility types",
  },
];

// Helper functions for mapping
export const getDisplayNameFromDbValue = (dbValue: string): string => {
  const mapping = FACILITY_TYPE_MAPPINGS.find((m) => m.dbValue === dbValue);
  return (
    mapping?.displayName ||
    dbValue.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

export const getDbValueFromDisplayName = (displayName: string): string => {
  const mapping = FACILITY_TYPE_MAPPINGS.find(
    (m) => m.displayName === displayName
  );
  return mapping?.dbValue || displayName.toLowerCase().replace(/\s+/g, "_");
};

export const getDbValuesFromDisplayNames = (
  displayNames: string[]
): string[] => {
  return displayNames.map(getDbValueFromDisplayName);
};

export const getDisplayNamesFromDbValues = (dbValues: string[]): string[] => {
  return dbValues.map(getDisplayNameFromDbValue);
};

// Get all display names grouped by category
export const getFacilityTypesByCategory = (): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {};

  FACILITY_TYPE_MAPPINGS.forEach((mapping) => {
    if (!grouped[mapping.category]) {
      grouped[mapping.category] = [];
    }
    grouped[mapping.category].push(mapping.displayName);
  });

  return grouped;
};

// Get all display names for dropdown
export const getAllFacilityTypeDisplayNames = (): string[] => {
  return FACILITY_TYPE_MAPPINGS.map((m) => m.displayName).sort();
};

// Check if a facility matches selected types (supports multiple types per facility)
export const facilityMatchesTypes = (
  facilityTypes: string[],
  selectedDisplayNames: string[]
): boolean => {
  if (selectedDisplayNames.length === 0) return true;

  const selectedDbValues = getDbValuesFromDisplayNames(selectedDisplayNames);
  return facilityTypes.some((type) => selectedDbValues.includes(type));
};

// Get category for a database value
export const getCategoryForDbValue = (dbValue: string): string => {
  const mapping = FACILITY_TYPE_MAPPINGS.find((m) => m.dbValue === dbValue);
  return mapping?.category || "Other";
};
